// CORE group — the chat product + the public/bare surfaces. No `_authed`/
// `_admin` guard: either fully public (community, pricing, s/v/d shares,
// screenshot), or self-gating like sign-in/sign-up will be once ported
// (mirrors app/sign-in/page.tsx's own auth()-based redirect-if-signed-in,
// not a route guard). ClientLayout's pathname-sniffing (routes/__root.tsx)
// already renders these chromeless where apps/web does today.
//
// OWNERSHIP: this file is the G1 porter's — chat shell + public/SEO group
// (FRONTEND_SPA_DESIGN.md §6, groups G1 + G4). Don't add G2 (authed CRUD) or
// G3 (admin/arena) routes here — those belong in agents.tsx / admin.tsx.
//
// Real routes (/, /chat/$sessionId) live in their own files and are just
// re-exported into this group's array. Everything else below is a
// PLACEHOLDER route (see route-helpers.tsx) — guard-free here by design,
// component swap is the only change needed to port each one for real.
//
// '/' and '/chat/$sessionId' are NOT siblings: both hang off the pathless
// chat-shell route, which renders the one shared GenUIChat instance. That is
// load-bearing — see routes/chat-shell.tsx (dev-flip P0-2).
import { rootRoute } from './__root';
import { placeholderRoute } from './route-helpers';
import { chatShellRoute } from './chat-shell';
import { indexRoute } from './index';
import { chatSessionRoute } from './chat.$sessionId';
import { communityRoute } from './community';
import { pricingRoute } from './pricing';
import { shareSessionRoute } from './share-session';
import { shareVisualizationRoute } from './share-visualization';
import { shareDashboardRoute } from './share-dashboard';
import { shareArtifactRoute } from './share-artifact';
import { signInSplatRoute, signinAliasRoute } from './sign-in';
import { signUpSplatRoute } from './sign-up';

export const coreRoutes = [
  chatShellRoute.addChildren([indexRoute, chatSessionRoute]),

  // Public/SEO surfaces (design doc §2 — Hono head-injection shell owns
  // <title>/meta/JSON-LD in prod; these routes own the body only).
  communityRoute,
  pricingRoute,
  shareSessionRoute,
  shareVisualizationRoute,
  shareDashboardRoute,
  shareArtifactRoute,
  // /screenshot/$id stays a placeholder — internal render-tooling surface
  // (apps/web/app/screenshot/[id]/page.tsx drives headless-screenshot
  // capture for render jobs), not a public share surface. Not in scope for
  // the /s /v /d share-pages + SEO-injector work (FRONTEND_SPA_DESIGN.md
  // §2 lists it alongside the other 4 but only /s /v /d /community got the
  // decision write-up; screenshot has no SEO/meta need — it's never linked
  // to a human).
  placeholderRoute(() => rootRoute, '/screenshot/$id'),

  // Auth entry (no guard — a signed-out visitor must be able to reach
  // these, or the `_authed` guard's redirect becomes a loop). Real Clerk
  // `routing="path"` forms (routes/sign-in.tsx / routes/sign-up.tsx); each
  // is a single `/$` splat route so both the bare path AND Clerk's own
  // path-routed sub-steps (SSO callback, factor-one, etc.) resolve instead
  // of 404ing. `/signin` (no dash) is a redirect-preserving-query alias,
  // parity with the old app's app/signin/page.tsx.
  signInSplatRoute,
  signUpSplatRoute,
  signinAliasRoute,

  // Dev utility routes (kept per §3a route tree listing).
  placeholderRoute(() => rootRoute, '/screenshot/test'),
  placeholderRoute(() => rootRoute, '/test-mermaid'),
];
